import { SEVERITY } from 'modules/entity/constants';

/**
 * @param {string} str
 * @returns {string}
 */
export const capitalizeAll = str => str.replace(/(^\w|\(\w)/g, (c) => c.toLocaleUpperCase());

/**
 * @param {string} languageCode
 * @param {string} [destinationLanguageCode]
 * @returns {string}
 */
export const getLanguageDisplayName = (languageCode, destinationLanguageCode = '') => {
  const normalized = languageCode.replaceAll('_', '-');
  const destination = destinationLanguageCode || normalized;
  const normalizedDestination = destination.replaceAll('_', '-');

  try {
    const displayNames = new Intl.DisplayNames([normalizedDestination], { type: 'language' });
    return capitalizeAll(displayNames.of(normalized) ?? languageCode);
  } catch {
    return languageCode;
  }
};

/**
 * @param {string} brandName
 * @returns {string}
 */
export const formatBrandName = (brandName) =>
  brandName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]|:|&/g, '')
    .replace(/(\s|-|')+/g, '_')
    .replace('_{2,}', '_')
    .toLowerCase();

export const getSeverityContent = (severity) => {
  switch (severity) {
    case SEVERITY.NONE:
    case SEVERITY.UNDEFINED:
    case 'SUCCESS':
      return 'common.ok';
    case 'YES':
      return 'common.yes';
    case SEVERITY.OFFERED:
      return 'web.offered';
    case SEVERITY.HIGH:
      return 'web.urgent';
    case 'FAILURE':
      return 'common.error';
    case 'NO':
      return 'common.no';
    case SEVERITY.LOW:
      return 'web.advisor';
    default:
      return severity;
  }
};

export const toPureBase64 = base64 => {
  const b64Data = base64.split(';');
  const b64 = b64Data[1];
  return b64.replace('base64,', '');
};

export const replaceUpperCaseParams = search =>
  search
    .replace(/PROVIEW=(TRUE|true)/g, 'proview=true')
    .replace(/PREVIEW=(TRUE|true)/g, 'preview=true')
    .replace(/LANGUAGE=(.*)/g, 'language=$1');

/**
 * Get the filename of a media object.
 * If the filename is not present, it will try to extract it from the path.
 * If the filename is not present in the path, it will return the current date.
 * @template {EnumMediaType} T
 * @param {import('types/Medias').Media<T>} media - The media object
 * @returns {string} filename - The filename of the media
 */
export const getFilename = media => {
  let filename;
  const { path, filename: mediaFilename, name: mediaName } = media ?? {};

  filename = mediaFilename ?? mediaName;
  if (!filename) {
    try {
      const url = new URL(path);
      const contentDisposition = url.searchParams.get('response-content-disposition');
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch.length === 2) {
          filename = fileNameMatch.pop();
        }
      }
    } catch {
      // Invalid URL (not signed usually)
    }
  }

  if (!filename) {
    filename = Date.now();
  }

  return filename;
};

/**
 * @param {string} filename
 * @returns {{name: string, extension: string}}
 */
export const getFilenameAndExtension = filename => {
  const result = /(.*)\.([^.]+)$/.exec(filename);
  if (result) {
    const [_, name, extension] = result;
    return { name, extension };
  }
  return { name: filename };
};
